import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaBalanceScale, FaFileContract, FaShieldAlt, FaGavel, FaBuilding, FaHandshake, FaExchangeAlt, FaFileAlt } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceSection = styled.div`
  margin-top: 30px;
  margin-bottom: 40px;
`;

const ServiceCategory = styled.div`
  margin-bottom: 30px;
`;

const ServiceTitle = styled.h4`
  color: #228B22;
  margin-bottom: 15px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ServiceList = styled.ul`
  list-style-type: none;
  padding-left: 10px;
`;

const ServiceItem = styled.li`
  margin-bottom: 10px;
  position: relative;
  padding-left: 20px;
  
  &:before {
    content: "•";
    color: #E60000;
    font-weight: bold;
    position: absolute;
    left: 0;
  }
`;

const ApproachSection = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 8px;
  margin: 30px 0;
`;

const ApproachList = styled.ul`
  list-style-type: none;
  padding-left: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const ApproachItem = styled.li`
  background: white;
  padding: 15px;
  border-radius: 6px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ConsultationButton = styled.button`
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 20px 0;
  
  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const FAQContainer = styled.div`
  width: 100%;
  background: white;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
`;

const FAQItem = styled.div`
  background: #ffffff;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 15px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: #E60000;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const LegalConsulting = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
    {
      question: "Pourquoi un cabinet d'expertise comptable propose-t-il des conseils juridiques ?",
      answer: "Les experts-comptables ont une vision globale de l'entreprise, intégrant les aspects financiers, fiscaux et juridiques. Cela permet une approche intégrée et une meilleure coordination des conseils."
    },
    {
      question: "Quelle est la différence entre les conseils juridiques d'un expert-comptable et ceux d'un avocat ?",
      answer: "Les avocats sont spécialisés dans la représentation en justice et le conseil juridique pur. Les experts-comptables offrent des conseils juridiques dans le cadre de leur expertise globale, souvent en lien avec les aspects financiers et fiscaux. Leur rôle est complémentaire, et ils peuvent travailler en partenariat avec des avocats."
    },
    {
      question: "Quels types de questions juridiques peuvent être traités par un cabinet d'expertise comptable ?",
      answer: "Les domaines couverts peuvent inclure le droit des sociétés, le droit commercial, le droit social (aspects liés à la paie), et le droit fiscal."
    },
    {
      question: "Comment les conseils juridiques s'intègrent-ils avec les autres services du cabinet ?",
      answer: "Les conseils juridiques sont souvent liés aux aspects comptables et fiscaux, permettant une approche globale et cohérente de la gestion d'entreprise."
    },
    {
      question: "Quels sont les services spécifiques que vous proposez en matière de droit des sociétés ?",
      answer: "Nous pouvons vous aider avec la création de sociétés, la modification des statuts, le suivi des obligations légales, et le conseil en matière de gouvernance."
    },
    {
      question: "Pouvez-vous m'assister dans la rédaction de contrats commerciaux ?",
      answer: "Nous pouvons vous conseiller sur la rédaction et la révision de contrats courants (contrats de vente, de prestation de services), en veillant à la conformité juridique et aux aspects financiers."
    },
    {
      question: "Offrez-vous des conseils en droit social ?",
      answer: "Nous pouvons vous conseiller sur les aspects légaux liés à la gestion du personnel, en particulier en lien avec la paie et les déclarations sociales."
    },
    {
      question: "Comment gérez-vous les situations de contentieux ?",
      answer: "Nous pouvons vous accompagner dans la phase précontentieuse, vous conseiller sur les options possibles, et vous orienter vers des avocats spécialisés si nécessaire."
    },
    {
      question: "Accompagnez-vous les entreprises lors de contrôles fiscaux ou sociaux ?",
      answer: "Oui, nous vous assistons durant les contrôles, et vous aidons à préparer les réponses aux administrations."
    },
  ];

  return (
    <>
      <Header />

      <section className="service-banner d-flex align-items-center">
        <div className="container text-white text-start">
          <h1 className="fw-bold">Conseils et Accompagnement Juridique</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/" className="text-white text-decoration-none">
                  Accueil
                </Link>{" "}
                /{" "}
                <Link to="/services" className="text-white text-decoration-none">
                  Services
                </Link>
              </li>
              <li className="breadcrumb-item active text-white" aria-current="page">
                Conseils Juridiques
              </li>
            </ol>
          </nav>
        </div>
      </section>

      <section className="service-content py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-start">
              <h2 className="fw-bold">Votre Accompagnement Juridique Global</h2>
              <p className="text-justify">
                CINEF-EC vous propose un accompagnement juridique global, couvrant tous les aspects de la vie de votre entreprise.
                Notre objectif est de vous apporter des solutions pragmatiques et efficaces, en parfaite adéquation avec vos besoins et vos objectifs.
              </p>
            </div>
          </div>

          <ServiceSection className="mt-4">
            <h3 className="mb-4">Nos Services Détaillés</h3>

            <ServiceCategory>
              <ServiceTitle>
                <FaBuilding size={20} color="#228B22" />
                Création d'entreprise
              </ServiceTitle>
              <ServiceList>
                <ServiceItem>Conseil sur le choix de la forme juridique la plus adaptée</ServiceItem>
                <ServiceItem>Rédaction des statuts et des pactes d'actionnaires</ServiceItem>
                <ServiceItem>Accomplissement des formalités d'immatriculation</ServiceItem>
              </ServiceList>
            </ServiceCategory>

            <ServiceCategory>
              <ServiceTitle>
                <FaHandshake size={20} color="#228B22" />
                Vie de l'entreprise
              </ServiceTitle>
              <ServiceList>
                <ServiceItem>Secrétariat juridique courant (assemblées générales, modifications statutaires, etc.)</ServiceItem>
                <ServiceItem>Rédaction et négociation de contrats (contrats commerciaux, baux commerciaux, contrats de travail, etc.)</ServiceItem>
                <ServiceItem>Conseil en droit social (relations individuelles et collectives du travail, embauche, licenciement, etc.)</ServiceItem>
                <ServiceItem>Gestion des contentieux (litiges commerciaux, recouvrement de créances, etc.)</ServiceItem>
              </ServiceList>
            </ServiceCategory>

            <ServiceCategory>
              <ServiceTitle>
                <FaExchangeAlt size={20} color="#228B22" />
                Opérations exceptionnelles
              </ServiceTitle>
              <ServiceList>
                <ServiceItem>Restructuration d'entreprise (fusion, acquisition, scission, apport partiel d'actif, transformation)</ServiceItem>
                <ServiceItem>Transmission d'entreprise (cession de parts sociales ou d'actions, cession de fonds de commerce)</ServiceItem>
                <ServiceItem>Dissolution et liquidation de sociétés</ServiceItem>
              </ServiceList>
            </ServiceCategory>

            <ServiceCategory>
              <ServiceTitle>
                <FaFileAlt size={20} color="#228B22" />
                Droit fiscal
              </ServiceTitle>
              <ServiceList>
                <ServiceItem>Conseil et optimisation de la fiscalité de l'entreprise et de ses dirigeants</ServiceItem>
                <ServiceItem>Assistance lors de contrôles fiscaux</ServiceItem>
              </ServiceList>
            </ServiceCategory>
          </ServiceSection>

          <ApproachSection>
            <h3 className="mb-4">Notre approche</h3>
            <ApproachList>
              <ApproachItem>
                <FaBalanceScale size={20} color="#228B22" />
                <span>Écoute et compréhension de vos enjeux</span>
              </ApproachItem>
              <ApproachItem>
                <FaFileContract size={20} color="#E60000" />
                <span>Analyse juridique rigoureuse</span>
              </ApproachItem>
              <ApproachItem>
                <FaGavel size={20} color="#228B22" />
                <span>Conseil clair et opérationnel</span>
              </ApproachItem>
              <ApproachItem>
                <FaShieldAlt size={20} color="#E60000" />
                <span>Accompagnement personnalisé et réactif</span>
              </ApproachItem>
            </ApproachList>
          </ApproachSection>

          <div className="text-start">
            <Link to="/contact">
              <ConsultationButton>Demander une consultation gratuite</ConsultationButton>
            </Link>
          </div>
        </div>
      </section>

      <section id="faq-section" className="container-fluid py-5 bg-light">
        <div className="container">
          <h3 className="mb-4">Foire Aux Questions</h3>
          <FAQContainer>
            {faqData.map((faq, index) => (
              <FAQItem key={index} delay={`${index * 0.1}s`}>
                <Question onClick={() => toggleFAQ(index)}>
                  {faq.question}
                  <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                </Question>
                {activeIndex === index && <Answer>{faq.answer}</Answer>}
              </FAQItem>
            ))}
          </FAQContainer>
        </div>
      </section>

      <Footer />

      <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.8), rgba(220, 53, 69, 0.8)), url("/assets/images/service-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 300px;
          display: flex;
          align-items: center;
        }
        
        .text-justify {
          text-align: justify;
        }
      `}</style>
    </>
  );
};

export default LegalConsulting;